import { AxiosAmbient } from 'src/lib';

import { FronteggContext } from '@frontegg/rest-api';
import { getEmbeddedEncounterId } from 'src/hooks/embedded/login';
import {
  getAppointmentByFilterSlice,
  getAppointmentsByStatusSlice,
} from 'src/store';
import { settingsOrgFlag } from 'src/store/actions/appointment.action';
import { showToastError } from 'src/utilities/errortoast';
import { showToastSuccess } from 'src/utilities/toast';
import { getCurrentPractitionerEntitlements } from '../../../../store/actions/practitioner.action';
import {
  encounterCompleteLoading,
  encounterCreateLoading,
  encounterDetailsLoading,
  encounterDetailsSlice,
  encounterListCountSlice,
  encounterListLoading,
  encounterListSlice,
  encounterRemoveLoading,
  getAuraDemoConversationSlice,
  newEncounterfromMicSlice,
  prerecordedTranscriptSlice,
  prerecordedTranscriptSpeakersSlice,
  selectedEncounterSlice,
  setMobileRecord,
  transcriptionLoading,
  transcriptionbyIdSlice,
  deletedEncountersSlice,
  deletedEncountersLoadingSlice,
  prerecordedTranscriptIdSlice,
  encounterUpdateLoading,
  scribePractitionersSlice,
  totalEncounterWillBeArchivedSlice,
  pinnedEncounterListLoading,
  pinnedEncounterListSlice,
  pinnedEncounterListCountSlice,
  encountersCSVColumnMappingsSlice,
  encountersCSVDateTimeFormatsSlice,
  encountersCSVColumnMappingsLoadingSlice,
  encountersCSVDateTimeFormatsLoadingSlice,
} from '../slices/encounter';
import { getSummary } from './summary';
import { getQuotaAction } from 'src/store/actions/quota.action';

const getUpdatedEncounterList = (encounterList, newEncounter) => {
  return (encounterList || []).map((encounter) => {
    if (encounter.encounter_id === newEncounter.encounter_id) {
      return newEncounter;
    }
    return encounter;
  });
};

export const listEncounters = (searchFilters, showLoader) => {
  return async (dispatch) => {
    if (showLoader) {
      dispatch(encounterListLoading(true));
      dispatch(newEncounterfromMicSlice(false));
    }
    dispatch(getAppointmentByFilterSlice([]));
    dispatch(getAppointmentsByStatusSlice([]));

    const filteredSearchParams = { ...searchFilters };
    delete filteredSearchParams.patientName;

    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };

    const queryParams = new URLSearchParams(filteredSearchParams).toString();
    try {
      const result = await AxiosAmbient.get(`/encounters?${queryParams}`, {
        headers,
      });
      const data = result?.data;

      if (result?.status === 200) {
        dispatch(encounterListSlice(data?.encounters));
        dispatch(encounterListCountSlice(data?.total));
        dispatch(
          totalEncounterWillBeArchivedSlice(
            data?.total_encounter_will_be_archived
          )
        );
        return data.encounters;
      } else {
        showToastError('Failed to load encounters');
      }
    } catch (error) {
      showToastError('Failed to load encounters');
    } finally {
      dispatch(encounterListLoading(false));
    }
  };
};

export const listPinnedEncounters = (showLoader) => {
  return async (dispatch) => {
    const ehrId = getEmbeddedEncounterId();

    if (!ehrId) {
      dispatch(pinnedEncounterListSlice([]));
      dispatch(pinnedEncounterListCountSlice(0));
      return [];
    }

    if (showLoader) {
      dispatch(pinnedEncounterListLoading(true));
    }

    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };

    try {
      const result = await AxiosAmbient.get(
        `/encounters?encounter_ehr_id=${encodeURIComponent(ehrId)}`,
        { headers }
      );

      if (result?.status === 200) {
        const data = result?.data;
        const encounters = data?.encounters || [];
        dispatch(pinnedEncounterListSlice(encounters));
        dispatch(pinnedEncounterListCountSlice(data?.total || 0));
        dispatch(pinnedEncounterListLoading(false));
        return encounters;
      } else {
        dispatch(pinnedEncounterListSlice([]));
        dispatch(pinnedEncounterListCountSlice(0));
      }
    } catch (error) {
      // 404 means no encounter found with that EHR ID - that's okay
      if (error?.response?.status !== 404) {
        console.error('Error fetching pinned encounters:', error);
      }
      dispatch(pinnedEncounterListSlice([]));
      dispatch(pinnedEncounterListCountSlice(0));
    }

    dispatch(pinnedEncounterListLoading(false));
  };
};

export const createDemoEncounter = (
  practitioner_id,
  appointment_id = null,
  callback
) => {
  return async (dispatch) => {
    const current_date = new Date();
    const formattedDate = `${current_date.getMonth() + 1}/${current_date.getDate()}`;
    const defaultDescription = `Encounter Demo - ${formattedDate}`;
    const data = {
      description: defaultDescription,
      appointment_id: appointment_id,
      practitioner_id: practitioner_id,
      start_time: new Date(),
    };
    return dispatch(createEncounter(data, callback));
  };
};

export const createEncounter = (data, callback) => {
  return async (dispatch) => {
    dispatch(encounterCreateLoading(true));
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    try {
      const result = await AxiosAmbient.post(`/encounters`, data, { headers });
      const response = result?.data;
      if (result?.status === 201) {
        dispatch(encounterDetailsSlice(response));
        dispatch(getQuotaAction());
        callback?.(response);
      } else {
        showToastError('Failed to create encounter');
      }
    } catch (exception) {
      showToastError(`Error creating encounter: ${exception.message}`);
    } finally {
      dispatch(encounterCreateLoading(false));
    }
  };
};

export const completeEncounter = (
  encounter_id,
  encounterPhase,
  storedParams,
  uploadAudio = false,
  callback
) => {
  return async (dispatch) => {
    dispatch(encounterCompleteLoading(true));
    try {
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
      };
      const params = {
        encounter_phase: encounterPhase,
        audio_uploaded: uploadAudio,
      };

      const result = await AxiosAmbient.post(
        `/encounters/${encounter_id}/complete`,
        {},
        { headers, params }
      );
      const response = result?.data;
      dispatch(encounterDetailsSlice(response));

      await dispatch(listEncounters(storedParams));
      dispatch(getEncounter(encounter_id, callback));
      showToastSuccess('Generated note will be available shortly.');
    } catch (error) {
      showToastError('Failed to complete encounter');
    }

    dispatch(encounterCompleteLoading(false));
  };
};

export const getEncounter = (encounter_id, callback) => {
  return async (dispatch) => {
    dispatch(encounterDetailsLoading(true));
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    let response;
    try {
      const result = await AxiosAmbient.get(`/encounters/${encounter_id}`, {
        headers,
      });
      response = result?.data;
      dispatch(encounterDetailsSlice(response));

      if (response?.encounter_status === 'completed' && response?.summary_id) {
        dispatch(getSummary(response?.summary_id));
      }

      callback?.(response);
    } catch (error) {
      // Silenced: getEncounter is called from polling intervals and
      // background refreshes; surfacing transient failures as toasts
      // spams the user. Callers that need error UI handle it themselves.
    }
    dispatch(encounterDetailsLoading(false));
    dispatch(encounterUpdateLoading(false));
    return response;
  };
};

export const startEncounter = (
  encounter_id,
  encounter_phase,
  data,
  params,
  callback
) => {
  return async (dispatch) => {
    dispatch(encounterDetailsLoading(true));
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const query_params = {
      encounter_phase: encounter_phase,
    };
    try {
      await AxiosAmbient.post(`/encounters/${encounter_id}/start`, data, {
        params: query_params,
        headers,
      });

      dispatch(getEncounter(encounter_id, callback));
      dispatch(listEncounters(params));
      dispatch(listPinnedEncounters());
    } catch (error) {
      showToastError('Failed to start encounter');
      showToastError(`Error starting encounter: ${error.message}`);
    }
    dispatch(encounterDetailsLoading(false));
  };
};

export const updateEncounter = (
  encounter_id,
  encounterPhase,
  data,
  params,
  showToastMessage = true
) => {
  return async (dispatch, getState) => {
    dispatch(encounterDetailsLoading(true));
    dispatch(encounterUpdateLoading(true));
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const query_params = {
      encounter_phase: encounterPhase,
    };

    const result = await AxiosAmbient.put(`/encounters/${encounter_id}`, data, {
      headers,
      params: query_params,
    });
    const response = result?.data;
    if (result?.status === 200) {
      if (showToastMessage) {
        showToastSuccess('Updated encounter');
      }

      const encounterList = getState().encounterState.encounterList || [];
      if (encounterList.length > 0) {
        dispatch(
          encounterListSlice(getUpdatedEncounterList(encounterList, response))
        );
      }
    } else {
      showToastError('Failed to update encounters');
    }
    dispatch(encounterDetailsLoading(false));
    dispatch(encounterUpdateLoading(false));

    return response;
  };
};

export const removeEncounter = (
  encounter_id,
  storedParams,
  setSubmitLoading,
  showToastMessage = true
) => {
  return async (dispatch) => {
    dispatch(encounterRemoveLoading(true));
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const result = await AxiosAmbient.delete(`/encounters/${encounter_id}`, {
      headers,
    });
    if (result?.status === 200 && showToastMessage) {
      dispatch(listEncounters(storedParams, true));
      dispatch(selectedEncounterSlice(null));

      showToastSuccess('Removed encounter');
      setSubmitLoading(false);
    } else {
      if (showToastMessage) {
        showToastError('Failed to delete encounter');
      }
    }
    dispatch(encounterRemoveLoading(false));
  };
};
export const getTranscription = (
  encounter_id,
  encounter_phase,
  type = 'live'
) => {
  return async (dispatch) => {
    dispatch(transcriptionLoading(true));
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const params = {
      encounter_id: encounter_id,
      encounter_phase: encounter_phase,
      type: type,
    };
    const result = await AxiosAmbient.get(`/transcriptions`, {
      headers,
      params,
    });
    const response = result?.data?.transcriptions;
    if (result?.status === 200) {
      if (type === 'prerecorded') {
        if (response?.length > 0 && response[0]?.status == 'ready') {
          dispatch(prerecordedTranscriptIdSlice(response[0].transcription_id));
          dispatch(prerecordedTranscriptSlice(response[0].transcription_json));
          dispatch(
            prerecordedTranscriptSpeakersSlice(
              response[0].transcription_speakers
            )
          );
        } else {
          dispatch(prerecordedTranscriptIdSlice(''));
          dispatch(prerecordedTranscriptSlice([]));
          dispatch(prerecordedTranscriptSpeakersSlice([]));
        }
      } else {
        dispatch(transcriptionbyIdSlice(response));
      }
    } else {
    }
    dispatch(transcriptionLoading(false));
  };
};

export const updateTranscription = (
  transcription_id,
  data,
  { onSuccess, onError }
) => {
  return async (dispatch) => {
    dispatch(transcriptionLoading(true));
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const result = await AxiosAmbient.put(
      `/transcriptions/${transcription_id}`,
      data,
      {
        headers,
      }
    );
    const response = result?.data;
    if (result?.status === 200) {
      dispatch(transcriptionbyIdSlice([response]));
      onSuccess(response);
    } else {
      onError;
    }
    dispatch(transcriptionLoading(false));
  };
};

export const AuraDemoEncounter = () => {
  return async (dispatch) => {
    dispatch(encounterDetailsLoading(true));
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const result = await AxiosAmbient.put(`/onboarding_demo`, {}, { headers });
    if (result?.status === 200) {
      await dispatch(settingsOrgFlag());
      await dispatch(getCurrentPractitionerEntitlements());
    }
    dispatch(encounterDetailsLoading(false));
  };
};

export const specialtiesConversation = () => {
  return async (dispatch) => {
    dispatch(transcriptionLoading(true));
    const token = FronteggContext.getAccessToken();
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    };
    try {
      const result = await AxiosAmbient.get('/onboarding/specialty', {
        headers,
      });
      const response = result?.data?.script?.conversation;
      if (result?.status === 200 && response) {
        dispatch(getAuraDemoConversationSlice(response));
      }
    } catch (error) {
      console.error('Failed to fetch conversation:', error, error);
    } finally {
      dispatch(transcriptionLoading(false));
    }
  };
};

export const syncEncounters = (data) => {
  return async (dispatch) => {
    const token = FronteggContext.getAccessToken();
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    };
    try {
      const result = await AxiosAmbient.post('/bulk_encounters', data, {
        headers,
      });
      if (result?.status === 200) {
        return result.data;
      } else {
        showToastError('Failed to upload encounters');
      }
    } catch (error) {
      console.error(error);
      showToastError(`Error uploading encounters: ${error.message}`);
    }
  };
};

export const getEncountersCSVColumnMappings = () => {
  return async (dispatch) => {
    try {
      dispatch(encountersCSVColumnMappingsLoadingSlice(true));
      const token = FronteggContext.getAccessToken();
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      };
      const result = await AxiosAmbient.get('/bulk_encounters/settings', {
        headers,
      });
      const response = result?.data;
      const column_mapping = response?.settings?.column_mapping;
      if (result?.status === 200) {
        dispatch(encountersCSVColumnMappingsSlice(column_mapping || {}));
        dispatch(
          encountersCSVDateTimeFormatsSlice({
            date_format: response?.settings?.date_format ?? '%m/%d/%Y',
            time_format: response?.settings?.time_format ?? '%H:%M',
            birth_date_format:
              response?.settings?.birth_date_format ?? '%m/%d/%Y',
          })
        );
      } else {
        dispatch(encountersCSVColumnMappingsSlice({}));
        dispatch(encountersCSVDateTimeFormatsSlice({}));
      }
    } catch (e) {
      dispatch(encountersCSVColumnMappingsSlice({}));
      dispatch(encountersCSVDateTimeFormatsSlice({}));
    } finally {
      dispatch(encountersCSVColumnMappingsLoadingSlice(false));
    }
  };
};

export const getDeletedEncounters = (searchFilters) => {
  return async (dispatch) => {
    dispatch(deletedEncountersLoadingSlice(true));
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };

    const queryParams = new URLSearchParams(searchFilters).toString();
    const result = await AxiosAmbient.get(`/encounters?${queryParams}`, {
      headers,
    });

    const response = result?.data;
    if (result?.status === 200) {
      dispatch(deletedEncountersSlice(response));
    } else {
      showToastError('Failed to load encounters');
    }
    dispatch(deletedEncountersLoadingSlice(false));
  };
};

export const restoreEncounter = (encounter_id) => {
  return async (dispatch) => {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };

    const result = await AxiosAmbient.put(
      `/encounters/${encounter_id}/restore`,
      {},
      {
        headers,
      }
    );
    if (result?.status === 200) {
      showToastSuccess('Encounter restored');
    } else {
      showToastError('Failed to restore encounter');
    }
  };
};

export const getScribePractitioners = () => {
  return async (dispatch) => {
    try {
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
      };
      const result = await AxiosAmbient.get('/practitioners/scribe', {
        headers,
      });
      if (result?.status === 200) {
        dispatch(scribePractitionersSlice(result?.data?.scribe_practitioners));
      } else {
        showToastError('Failed to fetch scribe practitioners');
      }
    } catch (error) {
      console.error(error);
      showToastError(`Error fetching scribe practitioners: ${error.message}`);
    }
  };
};

export const getEncounterByEHRID = (ehrId) => {
  return async (dispatch) => {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    try {
      const result = await AxiosAmbient.get(`/encounters/ehr/${ehrId}`, {
        headers,
      });
      const response = result?.data;
      if (result?.status === 200) {
        return response;
      }
      return null;
    } catch (error) {
      if (error?.response?.status === 404) {
        return null;
      }
      console.error('Failed to load encounter by EHR ID:', error);
      return null;
    }
  };
};
